import React, { useState } from "react";
import { MapPin, UserCircle } from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import SpinnerIcon from "../../../assets/svgs/spinner";

interface CustomAdsData {
  id: string;
  image: string;
  sponsorname: string;
  addressdetail: string;
  sponsoremail: string;
  sponsorephone: string;
  latitude: number;
  longitude: number;
  url: string;
  publish: boolean;
}

interface AdsCardProps {
  item: CustomAdsData;
  onPublishStatusChange: (updatedAd: CustomAdsData) => void;
}

const AdsCard: React.FC<AdsCardProps> = ({ item, onPublishStatusChange }) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handlePublishToggle = async () => {
    setIsPublishing(true);
    try {
      const adRef = doc(db, "bannerAdd", item.id);
      const newPublishStatus = !item.publish;
      await updateDoc(adRef, { publish: newPublishStatus });
      onPublishStatusChange({ ...item, publish: newPublishStatus });
      alert(`Ad ${item.publish ? "unpublished" : "published"} successfully!`);
    } catch (error) {
      console.error("Error updating publish status:", error);
      alert("Failed to update publish status.");
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      {/* Main Ad Card */}
      <div
        className="relative w-full min-w-sm overflow-hidden bg-lightBackground cursor-pointer text-text rounded-3xl bg-bgCard group"
        onClick={() => setIsOpen(true)}
      >
        <div className="relative h-[180px] w-full overflow-hidden">
          {/* Image with zoom effect */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
            style={{
              backgroundImage: `url(${item.image})`,
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="mb-2 text-base font-medium">{item.sponsorname}</h3>

          {/* Location */}
          <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="h-4 w-4 text-gray-400" />
            <span className="line-clamp-1">{item.addressdetail}</span>
          </div>

          {/* Sponsor Email */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <UserCircle className="h-4 w-4 text-gray-400" />
            <span className="line-clamp-1">Email: {item.sponsoremail}</span>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
          <div className="bg-bg w-full max-w-md p-6 rounded-lg shadow-lg relative">
            {/* Close Button */}
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>

            {/* Modal Content */}
            <h2 className="text-xl font-bold text-text mb-4">
              Advertisement Details
            </h2>

            <img
              src={item.image}
              alt={item.sponsorname}
              className="w-full h-40 object-cover rounded-md mb-4"
            />

            <p>
              <strong>Address:</strong> {item.addressdetail}
            </p>
            <p>
              <strong>Email:</strong> {item.sponsoremail}
            </p>
            <p>
              <strong>Phone:</strong> {item.sponsorephone}
            </p>
            <p>
              <strong>Latitude:</strong> {item.latitude}
            </p>
            <p>
              <strong>Longitude:</strong> {item.longitude}
            </p>
            <p>
              <strong>URL:</strong>{" "}
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                {item.url}
              </a>
            </p>
            <p>
              <strong>Status:</strong>{" "}
              {item.publish ? "Published" : "Unpublished"}
            </p>

            {/* Publish/Unpublish Button */}
            <button
              onClick={handlePublishToggle}
              disabled={isPublishing}
              className={`w-full mt-4 px-4 py-2 text-white rounded-lg ${
                item.publish ? "bg-red-500" : "bg-green-500"
              } hover:opacity-80 transition-opacity`}
            >
              {isPublishing ? (
                <SpinnerIcon />
              ) : item.publish ? (
                "Click to Unpublish"
              ) : (
                "Click to Publish"
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdsCard;
